import type { DefaultOptions } from '@tanstack/react-query';
import { QueryClient } from '@tanstack/react-query';

export interface CreateMainServiceQueryClientOptions {
  /**
   * Additional default options merged on top of the standard defaults.
   * Useful for app-specific retry strategies or stale times.
   */
  defaultOptions?: DefaultOptions;
}

/**
 * Create a TanStack `QueryClient` pre-configured with sensible defaults for
 * the Infinex main service:
 *
 * Callers can extend or override the defaults by passing `defaultOptions`.
 *
 * @example
 * ```ts
 * // Minimal — uses all built-in defaults
 * const queryClient = createMainServiceQueryClient();
 *
 * // Override stale time globally
 * const queryClient = createMainServiceQueryClient({
 *   defaultOptions: { queries: { staleTime: 60_000 } },
 * });
 * ```
 */
export function createMainServiceQueryClient(
  options: CreateMainServiceQueryClientOptions = {}
): QueryClient {
  const { defaultOptions = {} } = options;

  return new QueryClient({
    defaultOptions: {
      ...defaultOptions,
      queries: {
        retry: (failureCount, error: Error) => {
          if (error?.message === 'UNAUTHORIZED') return false;
          return failureCount < 3;
        },
        ...defaultOptions.queries,
      },
    },
  });
}
